import React, { useEffect, useState } from 'react';

import { Box, Flex, Text, Spinner, Button } from '@vimeo/bokeh';
import { ChevronRightSmall } from '@vimeo/bokeh-icons';

import { vimeoHCClient } from '../api/vimeoHC';
import { useHelpPanel } from '../hooks/useHelpPanel';
import { useVimeoHC } from '../hooks/useVimeoHC';
import { HelpArticle, HelpCategory } from '../types';
import { useTrackHelpPanelEvent } from '../utils/bigPicture';

export const ArticleListPanel: React.FC = () => {
  const { goToArticle, data } = useHelpPanel();
  const trackHelpPanelEvent = useTrackHelpPanelEvent();
  const sectionData: HelpCategory = data.article_list || {
    id: '',
    url: '',
    title: '',
    parentSectionId: '',
  };
  const { loading, error, execute } = useVimeoHC(vimeoHCClient.getArticlesBySectionId);

  const [articles, setArticles] = useState<HelpArticle[] | null>(null);

  useEffect(() => {
    if (sectionData.id) {
      const controller = new AbortController();
      execute(sectionData.id, controller.signal).then((result) => {
        if (result) {
          setArticles(result);
        }
      });
      return () => controller.abort();
    }
  }, [sectionData.id, execute]);

  if (loading || articles === null) {
    return (
      <Flex direction="column" alignItems="center" justifyContent="center" height="100%">
        <Spinner size="lg" />
      </Flex>
    );
  }

  if (error) {
    return (
      <Box p={4} color="status-destructive-primary">
        Failed to load section content. Please try again.
      </Box>
    );
  }

  if (articles.length === 0) {
    return <Box p={4}>No articles found.</Box>;
  }

  return (
    <Flex direction="column" gap="sm">
      {articles.map((article) => (
        <Flex
          as={Button}
          key={article.id}
          variant="tertiary"
          borderRadius="md"
          p="sm"
          height="100%"
          alignItems="flex-start"
          onClick={() => {
            goToArticle(article);
            trackHelpPanelEvent({
              eventName: 'vimeo.in_app_support_article_click',
              element: 'button',
              copy: article.title,
              flow: 'tile_click',
            });
          }}
        >
          <Text
            flex="1"
            variant="body-md"
            textAlign="left"
            whiteSpace="nowrap"
            overflow="hidden"
            textOverflow="ellipsis"
            minWidth="0"
          >
            {article.title}
          </Text>
          <ChevronRightSmall />
        </Flex>
      ))}
    </Flex>
  );
};
